import React, { useEffect, useMemo, useState } from "react";
import { api, fmt } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Printer, FileDown, Loader2 } from "lucide-react";

const MONTHS_TR_LONG = [
    "Ocak",
    "Şubat",
    "Mart",
    "Nisan",
    "Mayıs",
    "Haziran",
    "Temmuz",
    "Ağustos",
    "Eylül",
    "Ekim",
    "Kasım",
    "Aralık",
];
const METHOD = { cash: "Nakit", card: "Kredi Kartı", transfer: "Havale" };

function fmtTL(n) {
    return Number(n || 0).toLocaleString("tr-TR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}
function pad(n) {
    return String(n).padStart(2, "0");
}

export default function Reports() {
    const today = new Date();
    const defaultMonth = `${today.getFullYear()}-${pad(today.getMonth() + 1)}`;
    const [month, setMonth] = useState(defaultMonth);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const monthOptions = useMemo(() => {
        const out = [];
        const d = new Date();
        for (let i = 0; i < 12; i++) {
            const y = d.getFullYear();
            const m = d.getMonth() + 1;
            out.push({
                key: `${y}-${pad(m)}`,
                label: `${MONTHS_TR_LONG[m - 1]} ${y}`,
            });
            d.setMonth(d.getMonth() - 1);
        }
        return out;
    }, []);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get(`/clinic/report?month=${month}`);
            setData(data);
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [month]);

    const monthLabel = useMemo(() => {
        const [y, m] = month.split("-");
        return `${MONTHS_TR_LONG[parseInt(m, 10) - 1]} ${y}`;
    }, [month]);

    return (
        <div data-testid="reports-page">
            <div className="flex items-center justify-between mb-8 flex-wrap gap-3 no-print">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">
                        Aylık Rapor
                    </h1>
                    <p className="text-slate-600 mt-1">
                        Muhasebecinize verebileceğiniz ayrıntılı gelir-gider
                        raporu — PDF olarak indirin
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={month} onValueChange={setMonth}>
                        <SelectTrigger
                            className="w-48"
                            data-testid="report-month-select"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {monthOptions.map((o) => (
                                <SelectItem key={o.key} value={o.key}>
                                    {o.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button
                        onClick={() => window.print()}
                        className="dk-btn-primary"
                        data-testid="report-pdf-btn"
                    >
                        <FileDown className="w-4 h-4 mr-1" />
                        PDF İndir
                    </Button>
                </div>
            </div>

            {loading || !data ? (
                <div className="flex justify-center py-16">
                    <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
                </div>
            ) : (
                <div className="print-area bg-white rounded-2xl p-8 print:p-0 print:shadow-none shadow-sm border border-slate-200">
                    {/* Header */}
                    <div className="flex items-start justify-between pb-6 border-b border-slate-200">
                        <div>
                            <h2 className="text-2xl font-bold text-slate-900">
                                {data.clinic_name}
                            </h2>
                            <p className="text-slate-600 mt-1">
                                Aylık Gelir-Gider Raporu
                            </p>
                        </div>
                        <div className="text-right">
                            <div className="text-xs uppercase tracking-widest text-slate-500">
                                Dönem
                            </div>
                            <div className="text-2xl font-bold text-teal-700">
                                {monthLabel}
                            </div>
                        </div>
                    </div>

                    {/* Totals */}
                    <div className="grid grid-cols-3 gap-4 mt-6">
                        <div className="rounded-xl p-4 bg-emerald-50 border border-emerald-100">
                            <div className="text-xs uppercase tracking-wider font-medium text-emerald-700">
                                Toplam Gelir
                            </div>
                            <div className="text-2xl font-bold mono text-emerald-800 mt-1">
                                ₺{fmtTL(data.totals.revenue_try)}
                            </div>
                        </div>
                        <div className="rounded-xl p-4 bg-rose-50 border border-rose-100">
                            <div className="text-xs uppercase tracking-wider font-medium text-rose-700">
                                Toplam Gider
                            </div>
                            <div className="text-2xl font-bold mono text-rose-800 mt-1">
                                ₺{fmtTL(data.totals.expenses_try)}
                            </div>
                        </div>
                        <div
                            className={`rounded-xl p-4 border ${data.totals.profit_try >= 0 ? "bg-teal-50 border-teal-100" : "bg-amber-50 border-amber-100"}`}
                        >
                            <div
                                className={`text-xs uppercase tracking-wider font-medium ${data.totals.profit_try >= 0 ? "text-teal-700" : "text-amber-700"}`}
                            >
                                Net Kâr / Zarar
                            </div>
                            <div
                                className={`text-2xl font-bold mono mt-1 ${data.totals.profit_try >= 0 ? "text-teal-800" : "text-amber-800"}`}
                            >
                                ₺{fmtTL(data.totals.profit_try)}
                            </div>
                        </div>
                    </div>

                    {/* Payments */}
                    <div className="mt-8">
                        <h3 className="text-lg font-semibold text-slate-900 mb-3">
                            Tahsilatlar ({data.payments.length})
                        </h3>
                        <div className="border border-slate-200 rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-slate-50 border-b text-left text-xs uppercase tracking-wider text-slate-600">
                                        <th className="px-4 py-2">Tarih</th>
                                        <th className="px-4 py-2">Hasta</th>
                                        <th className="px-4 py-2">Tedavi</th>
                                        <th className="px-4 py-2">Yöntem</th>
                                        <th className="px-4 py-2 text-right">
                                            Tutar
                                        </th>
                                        <th className="px-4 py-2 text-right">
                                            TL Karşılığı
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.payments.map((p, i) => (
                                        <tr
                                            key={i}
                                            className="border-b last:border-b-0"
                                        >
                                            <td className="px-4 py-2 text-slate-600">
                                                {p.date}
                                            </td>
                                            <td className="px-4 py-2 font-medium">
                                                {p.patient_name}
                                            </td>
                                            <td className="px-4 py-2 text-slate-700">
                                                {p.treatment_title}
                                            </td>
                                            <td className="px-4 py-2 text-slate-600">
                                                {METHOD[p.method] ||
                                                    p.method}
                                            </td>
                                            <td className="px-4 py-2 text-right mono">
                                                {fmt(p.amount, p.currency)}
                                            </td>
                                            <td className="px-4 py-2 text-right mono font-semibold text-emerald-700">
                                                ₺{fmtTL(p.try_amount)}
                                            </td>
                                        </tr>
                                    ))}
                                    {data.payments.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={6}
                                                className="text-center py-6 text-slate-500"
                                            >
                                                Bu ay kayıtlı tahsilat yok
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                                {data.payments.length > 0 && (
                                    <tfoot>
                                        <tr className="bg-slate-50 border-t">
                                            <td
                                                colSpan={5}
                                                className="px-4 py-2 text-right font-semibold"
                                            >
                                                Toplam
                                            </td>
                                            <td className="px-4 py-2 text-right mono font-bold text-emerald-800">
                                                ₺
                                                {fmtTL(
                                                    data.totals.revenue_try,
                                                )}
                                            </td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    </div>

                    {/* Expenses */}
                    <div className="mt-8">
                        <h3 className="text-lg font-semibold text-slate-900 mb-3">
                            Giderler ({data.expenses.length})
                        </h3>
                        <div className="border border-slate-200 rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-slate-50 border-b text-left text-xs uppercase tracking-wider text-slate-600">
                                        <th className="px-4 py-2">Tarih</th>
                                        <th className="px-4 py-2">
                                            Kategori
                                        </th>
                                        <th className="px-4 py-2">
                                            Açıklama
                                        </th>
                                        <th className="px-4 py-2 text-right">
                                            Tutar
                                        </th>
                                        <th className="px-4 py-2 text-right">
                                            TL Karşılığı
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.expenses.map((e, i) => (
                                        <tr
                                            key={i}
                                            className="border-b last:border-b-0"
                                        >
                                            <td className="px-4 py-2 text-slate-600">
                                                {e.date}
                                            </td>
                                            <td className="px-4 py-2">
                                                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                                                    {e.category}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 text-slate-600">
                                                {e.description || "-"}
                                            </td>
                                            <td className="px-4 py-2 text-right mono">
                                                {fmt(e.amount, e.currency)}
                                            </td>
                                            <td className="px-4 py-2 text-right mono font-semibold text-rose-700">
                                                ₺{fmtTL(e.try_amount)}
                                            </td>
                                        </tr>
                                    ))}
                                    {data.expenses.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={5}
                                                className="text-center py-6 text-slate-500"
                                            >
                                                Bu ay kayıtlı gider yok
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                                {data.expenses.length > 0 && (
                                    <tfoot>
                                        <tr className="bg-slate-50 border-t">
                                            <td
                                                colSpan={4}
                                                className="px-4 py-2 text-right font-semibold"
                                            >
                                                Toplam
                                            </td>
                                            <td className="px-4 py-2 text-right mono font-bold text-rose-800">
                                                ₺
                                                {fmtTL(
                                                    data.totals.expenses_try,
                                                )}
                                            </td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="mt-10 pt-4 border-t border-slate-200 text-xs text-slate-500 flex items-center justify-between">
                        <span>
                            DentDefter — Diş Kliniği Ön Muhasebe SaaS
                        </span>
                        <span className="mono">
                            Oluşturulma:{" "}
                            {new Date().toLocaleString("tr-TR")}
                        </span>
                    </div>
                </div>
            )}

            <div className="mt-4 text-xs text-slate-500 no-print flex items-center gap-2">
                <Printer className="w-3.5 h-3.5" />
                "PDF İndir" tuşu tarayıcının yazdırma diyaloğunu açar. Hedef
                olarak "PDF olarak kaydet" seçin.
            </div>
        </div>
    );
}
